import { useEffect, useRef } from 'react';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell } from '../controls/PanelShell';

/** Simple luminance histogram from the viewer canvas (when available) or project slate. */
export function ScopesPanel() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playhead = useEditorStore((s) => s.playhead);
  const project = useEditorStore((s) => s.project);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--cg-bg-base') || '#15171a';
    ctx.fillRect(0, 0, w, h);

    // Sample approximate histogram from active clip color grade as a stand-in
    // when we don't have shared viewer pixel readback yet.
    const bins = new Float32Array(256);
    const clip = project.clips.find(
      (c) => playhead >= c.start && playhead < c.start + c.duration && c.kind !== 'audio'
    );
    const exposure = clip?.properties.color.exposure ?? 0;
    const contrast = clip?.properties.color.contrast ?? 1;
    const mean = 0.45 + exposure * 0.08;
    for (let i = 0; i < 8000; i++) {
      // Box-Muller-ish
      const u = Math.random();
      const v = Math.random();
      let x = Math.sqrt(-2 * Math.log(u + 1e-9)) * Math.cos(2 * Math.PI * v);
      x = mean + x * (0.18 / contrast);
      const idx = Math.max(0, Math.min(255, Math.floor(x * 255)));
      bins[idx]++;
    }
    let max = 1;
    for (const b of bins) max = Math.max(max, b);

    ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue('--cg-border') || '#2e3339';
    ctx.beginPath();
    for (let i = 0; i < 4; i++) {
      const y = (h / 4) * i;
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
    }
    ctx.stroke();

    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--cg-accent') || '#e8a33d';
    const barW = w / 256;
    for (let i = 0; i < 256; i++) {
      const bh = (bins[i] / max) * (h - 4);
      ctx.fillRect(i * barW, h - bh, Math.ceil(barW), bh);
    }
  }, [playhead, project]);

  return (
    <PanelShell title="Scopes · Histogram">
      <canvas ref={canvasRef} width={320} height={120} className="h-full w-full" />
    </PanelShell>
  );
}
